import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, Network, Calendar, FileSearch } from "lucide-react";
import { toast } from "sonner";
import { epsteinEntity } from "../lib/api";
import { KIND_META, REL_META, CONFIDENCE_META, EVIDENCE_GROUPS, EF_DISCLAIMER, deriveLegalLabel } from "../components/epstein/efConsts";
import { EvidenceFragments } from "../components/epstein/EvidenceFragments";
import { RecordMediaGallery } from "../components/media/PublishedMedia";

const EpsteinEntity = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const r = await epsteinEntity(slug);
        if (active) setData(r);
      } catch (e) {
        toast.error("File not found.");
        if (active) setData(null);
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [slug]);

  if (loading) {
    return <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center font-mono text-[#757065]">// LOADING FILE…</div>;
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[#0F0F0F] flex flex-col items-center justify-center gap-3" data-testid="ef-entity-404">
        <div className="font-mono text-xs text-[#757065]">// FILE NOT FOUND</div>
        <Link to="/epstein-files" className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">← back to the file</Link>
      </div>
    );
  }

  const { entity, relationships, sources, evidence_items = [], timeline_events = [], _under_development = false } = data;
  const meta = KIND_META[entity.kind] || KIND_META.reference;
  // Group evidence by type for the gallery
  const evidenceByType = {};
  evidence_items.forEach((ev) => {
    (evidenceByType[ev.evidence_type] ||= []).push(ev);
  });

  // Strip internal "review_tier=public-ready." editor flag from any note
  // before public rendering. Caveats that follow the flag stay intact.
  const cleanNote = (note) =>
    typeof note === "string"
      ? note.replace(/^review_tier=[a-z0-9_-]+\.?\s*/i, "").trim()
      : note;

  // Hide a child note when it is essentially the same as the entity-level
  // legal_note (the user's de-duplication request).
  const isDuplicateOfEntityNote = (note) => {
    if (!note || !entity.legal_note) return false;
    const a = cleanNote(note).toLowerCase();
    const b = cleanNote(entity.legal_note).toLowerCase();
    if (!a || !b) return false;
    return a.includes(b.slice(0, 40)) || b.includes(a.slice(0, 40));
  };

  // Split sources into Primary / Reporting buckets for clearer hierarchy on
  // pages like Ariane de Rothschild where DOJ records and media coverage are
  // mixed.
  const PRIMARY_ORIGINS = new Set([
    "DOJ", "Government", "Court", "FOIA",
    "doj", "government", "court", "foia",
  ]);
  const PRIMARY_SOURCE_TYPES = new Set([
    "doj_record", "doj_disclosure", "court_filing", "deposition",
    "government_record", "foia_document", "declassified_document",
    "press_release",
  ]);
  const isPrimarySource = (s) =>
    PRIMARY_ORIGINS.has(s.source_origin)
    || PRIMARY_SOURCE_TYPES.has(s.source_type);
  const primarySources = sources.filter(isPrimarySource);
  const reportingSources = sources.filter((s) => !isPrimarySource(s));

  // -------------------------------------------------------------------
  // Editorial-development stub.
  // The entity is on the public Evidence Board, but its long-form dossier
  // is still in draft/review. We show only safe basics: name, image,
  // kind/status chips, short summary if present, source count + source
  // trail — and a clear "under editorial development" notice.
  // -------------------------------------------------------------------
  if (_under_development) {
    const imgSrc = entity.image_url || entity.photo_url || entity.thumbnail_url;
    const statusLabel = (data.dossier_status || "draft").toUpperCase();
    return (
      <div
        className="min-h-screen bg-[#0F0F0F]"
        data-testid={`ef-entity-page-${entity.id}`}
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-10 py-6 md:py-10">
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <Link
              to="/epstein-files"
              data-testid="ef-back-to-board"
              className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] hover:text-[#D92D20]"
            >
              <ArrowLeft size={12} /> Back to evidence board
            </Link>
          </div>

          {/* Editorial development banner */}
          <div
            className="border border-[#E0A526]/50 bg-[#1a1408] px-4 py-3 mb-6 flex items-start gap-3"
            data-testid="ef-entity-under-development-banner"
          >
            <span className="inline-block w-2 h-2 bg-[#E0A526] mt-1.5 animate-pulse" />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E0A526] mb-1">
                // dossier · under editorial development · status: {statusLabel}
              </div>
              <div className="text-[12px] text-[#CBB892] leading-relaxed">
                This dossier is currently under editorial development. The
                long-form analysis is being researched and source-trailed and
                will appear here once cleared for publication. The entity is
                visible on the Evidence Board, and the approved source trail
                below remains available for reference.
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-[220px_1fr] gap-6 mb-8">
            <div>
              {imgSrc ? (
                <figure
                  className="border border-[#2D2A26] bg-[#0a0907]"
                  data-testid="ef-entity-image"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={imgSrc}
                      alt={entity.image_alt || entity.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {entity.image_source && (
                    <figcaption className="px-2 py-1.5 border-t border-[#2D2A26] font-mono text-[9px] uppercase tracking-[0.25em] text-[#7a7062] truncate">
                      // source · {entity.image_source}
                    </figcaption>
                  )}
                </figure>
              ) : (
                <div className="border border-dashed border-[#2D2A26] aspect-[4/3] bg-[#0a0907] flex flex-col items-center justify-center">
                  <div className="text-5xl" style={{ color: meta.color }}>{meta.icon}</div>
                  <div className="font-mono text-[8px] uppercase tracking-[0.25em] mt-2 text-[#757065]">
                    no image assigned
                  </div>
                </div>
              )}
              <div className="mt-3 grid grid-cols-2 gap-x-2 gap-y-1 font-mono text-[10px]">
                <div className="text-[#757065]">kind</div>
                <div className="text-[#E4DFD3]">{entity.kind}</div>
                <div className="text-[#757065]">sources</div>
                <div className="text-[#E4DFD3]" data-testid="ef-entity-source-count">{sources.length}</div>
              </div>
            </div>

            <div>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span
                  style={{ color: meta.color, borderColor: meta.color }}
                  className="font-mono text-[9px] uppercase tracking-[0.3em] border px-2 py-0.5"
                >
                  {meta.label}
                </span>
                {entity.category_label && (
                  <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#9FA8D4] border border-[#9FA8D4]/40 px-2 py-0.5">
                    {entity.category_label}
                  </span>
                )}
                {entity.is_anchor && (
                  <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D92D20] border border-[#D92D20] px-2 py-0.5">
                    // ANCHOR
                  </span>
                )}
                <span
                  className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#E0A526] border border-[#E0A526]/60 px-2 py-0.5"
                  data-testid="ef-entity-dossier-status-chip"
                >
                  // DOSSIER · {statusLabel}
                </span>
              </div>
              <h1 className="font-display text-3xl sm:text-4xl text-[#E4DFD3] mb-2">
                {entity.name}
              </h1>
              {entity.occupation_or_role && (
                <div className="text-sm text-[#A39E93] mb-2">
                  {entity.occupation_or_role}
                </div>
              )}
              {entity.summary && (
                <p
                  className="text-sm text-[#A39E93] leading-relaxed mt-2"
                  data-testid="ef-entity-basic-summary"
                >
                  {entity.summary}
                </p>
              )}
              <div className="border-l-2 border-[#E0A526] pl-3 mt-4 text-[11px] italic text-[#9b9485] leading-snug">
                {EF_DISCLAIMER}
              </div>
            </div>
          </div>

          {/* Source trail — kept visible; these are individually approved */}
          <section className="mt-8" data-testid="ef-entity-source-trail">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-3">
              // APPROVED SOURCE TRAIL · {sources.length}
            </div>
            {sources.length === 0 ? (
              <div className="border border-dashed border-[#2D2A26] p-6 text-center font-mono text-xs uppercase tracking-widest text-[#757065]">
                No public sources attached to this file yet.
              </div>
            ) : (
              <div className="space-y-2">
                {sources.map((s) => (
                  <div
                    key={s.id}
                    className="border border-[#2D2A26] bg-[#0F0F0F] px-3 py-2"
                    data-testid={`ef-entity-source-${s.id}`}
                  >
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="font-mono text-[8px] uppercase tracking-[0.25em] text-[#5DD4B3] border border-[#5DD4B3]/50 px-1.5 py-0.5">
                        {(s.source_type || "source").replace(/_/g, " ")}
                      </span>
                      {s.source_origin && (
                        <span className="font-mono text-[8px] uppercase tracking-[0.25em] text-[#9FA8D4] border border-[#9FA8D4]/50 px-1.5 py-0.5">
                          {s.source_origin}
                        </span>
                      )}
                      {s.publisher && (
                        <span className="font-mono text-[9px] text-[#757065]">
                          {s.publisher}
                        </span>
                      )}
                    </div>
                    <Link
                      to={`/epstein-files/source/${s.id}`}
                      className="block hover:text-[#D92D20]"
                    >
                      <div className="font-heading text-sm text-[#E4DFD3]">{s.title}</div>
                    </Link>
                    {s.url && (
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-1 font-mono text-[10px] text-[#A39E93] hover:text-[#D92D20] break-all"
                      >
                        {s.url} <ExternalLink size={9} />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F0F0F]" data-testid={`ef-entity-page-${entity.id}`}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-10 py-6 md:py-10">
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <Link to="/epstein-files" data-testid="ef-back-to-board"
            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] hover:text-[#D92D20]">
            <ArrowLeft size={12} /> Back to evidence board
          </Link>
        </div>

        <div className="grid md:grid-cols-[260px_1fr] gap-6">
          <div>
            {(() => {
              const imgSrc = entity.image_url || entity.photo_url || entity.thumbnail_url;
              return imgSrc ? (
                <figure className="border border-[#2D2A26] bg-[#0a0907]" data-testid="ef-entity-image">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={imgSrc}
                      alt={entity.image_alt || entity.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {entity.image_source && (
                    <figcaption className="px-2 py-1.5 border-t border-[#2D2A26] font-mono text-[9px] uppercase tracking-[0.25em] text-[#7a7062] truncate">
                      // source · {entity.image_source}
                    </figcaption>
                  )}
                </figure>
              ) : (
                <div className="border border-dashed border-[#2D2A26] aspect-[4/3] bg-[#0a0907] flex flex-col items-center justify-center">
                  <div className="text-6xl" style={{ color: meta.color }}>{meta.icon}</div>
                  <div className="font-mono text-[8px] uppercase tracking-[0.25em] mt-2 text-[#757065]">no image assigned</div>
                </div>
              );
            })()}
            <div className="mt-3 grid grid-cols-2 gap-x-2 gap-y-1 font-mono text-[10px]">
              <div className="text-[#757065]">kind</div><div className="text-[#E4DFD3]">{entity.kind}</div>
              <div className="text-[#757065]">connections</div><div className="text-[#E4DFD3]">{relationships.length}</div>
              <div className="text-[#757065]">sources</div><div className="text-[#E4DFD3]">{sources.length}</div>
            </div>
          </div>

          <div>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span style={{ color: meta.color, borderColor: meta.color }}
                className="font-mono text-[9px] uppercase tracking-[0.3em] border px-2 py-0.5">{meta.label}</span>
              {entity.category_label && (
                <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#9FA8D4] border border-[#9FA8D4]/40 px-2 py-0.5">
                  {entity.category_label}
                </span>
              )}
              {entity.is_anchor && (
                <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D92D20] border border-[#D92D20] px-2 py-0.5">// ANCHOR</span>
              )}
              {entity.confidence_level && (
                <span style={{ color: (CONFIDENCE_META[entity.confidence_level] || {}).color, borderColor: (CONFIDENCE_META[entity.confidence_level] || {}).color }}
                  className="font-mono text-[9px] uppercase tracking-[0.3em] border px-2 py-0.5">
                  CONF · {entity.confidence_level.toUpperCase()}
                </span>
              )}
              {entity.demo && (
                <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#E0A526] border border-[#E0A526]/50 px-2 py-0.5">// DEMO PLACEHOLDER</span>
              )}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl text-[#E4DFD3] mb-2">{entity.name}</h1>
            {entity.occupation_or_role && (
              <div className="text-sm text-[#A39E93] mb-2" data-testid="ef-entity-role">{entity.occupation_or_role}</div>
            )}
            {(entity.known_aliases && entity.known_aliases.length > 0) && (
              <div className="font-mono text-[10px] text-[#757065] mb-3">aliases: {entity.known_aliases.join(", ")}</div>
            )}

            {/* Authenticated Media Library photographs (published only) */}
            <RecordMediaGallery targetType="any" targetId={entity.slug} />

            {/* Place / property dossier — appears only when this entity has an
                approved dossier reachable at /epstein-files/places/:slug. */}
            {entity.kind === "property" && Array.isArray(entity.dossier_sections)
              && entity.dossier_sections.some((s) => s?.section_status === "approved") && (
              <Link
                to={`/epstein-files/places/${entity.slug}`}
                data-testid="ef-entity-open-dossier"
                className="inline-flex items-center gap-2 border border-[#D92D20] hover:bg-[#D92D20] hover:text-white text-[#D92D20] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.3em] mb-3"
              >
                <FileSearch size={12} /> Open property dossier <ArrowLeft size={12} className="rotate-180" />
              </Link>
            )}

            {/* B. Documented connection summary — short thesis block.
                Distinct from the deeper // CONNECTIONS card grid below; this
                is the one-paragraph "why this entity is in the graph". */}
            {entity.connection_summary_to_epstein && (
              <div
                className="border-l-2 border-[#D92D20] pl-3 mb-3"
                data-testid="ef-entity-connection-thesis"
              >
                <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D92D20] mb-1">
                  // CONNECTION TO EPSTEIN
                </div>
                <div className="text-sm text-[#E4DFD3] leading-relaxed">{entity.connection_summary_to_epstein}</div>
                {(entity.first_known_connection_date || entity.last_known_connection_date) && (
                  <div className="font-mono text-[10px] text-[#A39E93] mt-1">
                    {entity.first_known_connection_date || "?"} → {entity.last_known_connection_date || "?"}
                  </div>
                )}
              </div>
            )}

            {/* C. Editorial / legal note — single consolidated block that
                preserves the entity-level legal_note (EDR Group denial when
                relevant) AND the standing EF_DISCLAIMER. Replaces three
                stacked notes with one. */}
            <div
              className="border border-[#E0A526]/40 bg-[#1a1408] px-3 py-2 mb-3"
              data-testid="ef-entity-editorial-note"
            >
              <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#E0A526] mb-1">
                // EDITORIAL / LEGAL NOTE
              </div>
              {entity.legal_note && (
                <div className="text-xs text-[#CBB892] leading-relaxed mb-1.5">{cleanNote(entity.legal_note)}</div>
              )}
              <div className="text-[11px] italic text-[#9b9485] leading-snug">{EF_DISCLAIMER}</div>
            </div>

            {/* D. Biographical context — moved below the thesis + editorial
                note so the page no longer opens with a wall of dense prose. */}
            {(entity.summary || entity.full_description) && (
              <div data-testid="ef-entity-bio">
                <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mb-1">
                  // BIOGRAPHICAL CONTEXT
                </div>
                {entity.summary && (
                  <p className="text-sm text-[#A39E93] leading-relaxed mb-2">{entity.summary}</p>
                )}
                {entity.full_description && (
                  <p className="text-sm text-[#A39E93] leading-relaxed">{entity.full_description}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Document Gallery */}
        {evidence_items.length > 0 && (
          <section className="mt-10" data-testid="ef-entity-document-gallery">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-3 flex items-center gap-2">
              <FileSearch size={12} /> // DOCUMENT GALLERY · {evidence_items.length}
            </div>
            {EVIDENCE_GROUPS.map((g) => {
              const items = evidenceByType[g.key] || [];
              if (!items.length) return null;
              return (
                <div key={g.key} className="mb-4">
                  <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#5DD4B3] mb-1">{g.label} · {items.length}</div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {items.map((ev) => (
                      <div key={ev.id} className="border border-[#2D2A26] bg-[#0F0F0F] px-3 py-2"
                           data-testid={`ef-entity-evidence-${ev.id}`}>
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="font-mono text-[8px] uppercase tracking-[0.25em] text-[#5DD4B3] border border-[#5DD4B3]/40 px-1.5 py-0.5">
                            {ev.evidence_type}
                          </span>
                          {ev.date && <span className="font-mono text-[9px] text-[#A39E93]">{ev.date}</span>}
                          {ev.page_reference && <span className="font-mono text-[9px] text-[#757065]">{ev.page_reference}</span>}
                          {ev.confidence_level && (
                            <span style={{ color: (CONFIDENCE_META[ev.confidence_level] || {}).color }}
                              className="font-mono text-[8px] uppercase tracking-[0.25em] ml-auto">
                              {ev.confidence_level}
                            </span>
                          )}
                        </div>
                        <div className="font-heading text-sm text-[#E4DFD3]">{ev.title}</div>
                        {ev.publisher_or_origin && (
                          <div className="font-mono text-[10px] text-[#757065] mb-1">{ev.publisher_or_origin}</div>
                        )}
                        {ev.short_summary && (
                          <div className="text-xs text-[#A39E93] mt-1 line-clamp-2">{ev.short_summary}</div>
                        )}
                        {ev.legal_note && !isDuplicateOfEntityNote(ev.legal_note) && (
                          <div className="font-mono text-[9px] text-[#E0A526] mt-1 italic">⚠ {cleanNote(ev.legal_note)}</div>
                        )}
                        {ev.source_url && (
                          <a href={ev.source_url} target="_blank" rel="noopener noreferrer"
                             className="inline-flex items-center gap-1 mt-1 font-mono text-[10px] text-[#A39E93] hover:text-[#D92D20] break-all">
                            view source <ExternalLink size={9} />
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </section>
        )}

        {/* Timeline */}
        {timeline_events.length > 0 && (
          <section className="mt-8" data-testid="ef-entity-timeline">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-3 flex items-center gap-2">
              <Calendar size={12} /> // TIMELINE · {timeline_events.length}
            </div>
            <div className="space-y-2">
              {timeline_events.map((t) => (
                <div key={t.id} className="border-l-2 border-[#D92D20] pl-3 py-1"
                     data-testid={`ef-entity-timeline-${t.id}`}>
                  <div className="font-mono text-[10px] text-[#D92D20]">
                    {t.date || "—"}{t.exact_datetime ? ` · ${t.exact_datetime}` : ""}
                  </div>
                  <div className="text-sm text-[#E4DFD3]">{t.title}</div>
                  {t.location_label && <div className="font-mono text-[10px] text-[#757065]">{t.location_label}</div>}
                  {t.summary && <div className="text-xs text-[#A39E93] mt-1">{t.summary}</div>}
                  {t.legal_note && !isDuplicateOfEntityNote(t.legal_note) && (
                    <div className="font-mono text-[9px] text-[#E0A526] mt-1 italic">⚠ {cleanNote(t.legal_note)}</div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Relationships */}
        <section className="mt-10" data-testid="ef-entity-connections">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-3 flex items-center gap-2">
            <Network size={12} /> // CONNECTIONS · CASE FILE
          </div>
          {relationships.length === 0 ? (
            <div className="border border-dashed border-[#2D2A26] p-6 text-center font-mono text-xs uppercase tracking-widest text-[#757065]">
              No public connections recorded for this file yet.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-2">
              {relationships.map((r) => {
                const rm = REL_META[r.type] || REL_META.other_editor_defined;
                const cm = CONFIDENCE_META[r.confidence] || CONFIDENCE_META.medium;
                const ll = deriveLegalLabel(r);
                const other = r.other;
                const dateRange = r.date_label
                  || (r.start_date && (r.end_date ? `${r.start_date} → ${r.end_date}` : r.start_date))
                  || null;
                // Hide the per-rel legal_note when it merely repeats the
                // entity-level legal_note (de-duplication request).
                const showLegal = r.legal_note && !isDuplicateOfEntityNote(r.legal_note);
                const cleanLegal = showLegal ? cleanNote(r.legal_note) : null;
                return (
                  <div
                    key={r.id}
                    className="border border-[#2D2A26] bg-[#0F0F0F] px-3 py-2 hover:border-[#D92D20]/60"
                    data-testid={`ef-entity-rel-${r.id}`}
                  >
                    {/* Header line: counter-party (the case subject) */}
                    {other ? (
                      <Link to={`/epstein-files/entity/${other.slug}`} className="block hover:text-[#D92D20] mb-1">
                        <div className="font-heading text-sm text-[#E4DFD3]">{other.name}</div>
                        <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">{other.kind}</div>
                      </Link>
                    ) : (
                      <div className="text-[#757065] text-sm mb-1">(linked entity not public)</div>
                    )}

                    {/* Case-file grid: Relationship · Date range · Confidence · Class */}
                    <div
                      className="mt-2 grid gap-x-2 gap-y-1 font-mono text-[10px]"
                      style={{ gridTemplateColumns: "max-content 1fr" }}
                    >
                      <span className="text-[#757065] uppercase tracking-[0.2em]">relationship</span>
                      <span style={{ color: rm.color }} className="uppercase tracking-[0.2em]">{rm.label}</span>
                      {dateRange && (
                        <>
                          <span className="text-[#757065] uppercase tracking-[0.2em]">date range</span>
                          <span className="text-[#E4DFD3]">{dateRange}</span>
                        </>
                      )}
                      {ll && (
                        <>
                          <span className="text-[#757065] uppercase tracking-[0.2em]">record class</span>
                          <span style={{ color: ll.color }} className="uppercase tracking-[0.2em]">{ll.label}</span>
                        </>
                      )}
                      <span className="text-[#757065] uppercase tracking-[0.2em]">confidence</span>
                      <span style={{ color: cm.color }} className="uppercase tracking-[0.2em]">{cm.label}</span>
                      {r.location_label && (
                        <>
                          <span className="text-[#757065] uppercase tracking-[0.2em]">location</span>
                          <span className="text-[#A39E93]">{r.location_label}</span>
                        </>
                      )}
                    </div>

                    {/* Basis paragraph (the existing rel summary) */}
                    {r.summary && (
                      <div className="mt-2 text-xs text-[#A39E93] leading-relaxed">
                        <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065] mr-1.5">basis:</span>
                        {r.summary}
                      </div>
                    )}

                    {/* Per-rel editorial note — only when materially different
                        from the entity-level note above. */}
                    {cleanLegal && (
                      <div className="font-mono text-[9px] text-[#E0A526] mt-2 italic leading-snug">⚠ {cleanLegal}</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Evidence fragments — exact emails / pages / excerpts */}
        <EvidenceFragments
          entitySlug={entity.slug}
          testidPrefix="ef-entity-fragments"
        />

        {/* Sources — split into Primary records vs Reporting */}
        <section className="mt-8" data-testid="ef-entity-source-trail">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-3">// SOURCE TRAIL</div>
          {sources.length === 0 ? (
            <div className="border border-dashed border-[#2D2A26] p-6 text-center font-mono text-xs uppercase tracking-widest text-[#757065]">
              No public sources attached to this file yet.
            </div>
          ) : (() => {
            const renderSourceCard = (s) => (
              <div key={s.id} className="border border-[#2D2A26] bg-[#0F0F0F] px-3 py-2" data-testid={`ef-entity-source-${s.id}`}>
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="font-mono text-[8px] uppercase tracking-[0.25em] text-[#5DD4B3] border border-[#5DD4B3]/50 px-1.5 py-0.5">
                    {(s.source_type || "source").replace(/_/g, " ")}
                  </span>
                  {s.source_origin && (
                    <span className="font-mono text-[8px] uppercase tracking-[0.25em] text-[#9FA8D4] border border-[#9FA8D4]/50 px-1.5 py-0.5">
                      {s.source_origin}
                    </span>
                  )}
                  {s.publisher && <span className="font-mono text-[9px] text-[#757065]">{s.publisher}</span>}
                </div>
                <Link to={`/epstein-files/source/${s.id}`} className="block hover:text-[#D92D20]">
                  <div className="font-heading text-sm text-[#E4DFD3]">{s.title}</div>
                </Link>
                {s.excerpt && <div className="text-xs italic text-[#A39E93] mt-1 line-clamp-2">{s.excerpt}</div>}
                <a href={s.url} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1 mt-1 font-mono text-[10px] text-[#A39E93] hover:text-[#D92D20] break-all">
                  {s.url} <ExternalLink size={9} />
                </a>
              </div>
            );
            // If the bucket split would produce two groups, render both; if
            // everything ends up in one bucket (e.g. legacy entities with no
            // primary records yet) just render a flat list to avoid an
            // awkward empty heading.
            const hasSplit = primarySources.length > 0 && reportingSources.length > 0;
            if (!hasSplit) {
              return <div className="space-y-2">{sources.map(renderSourceCard)}</div>;
            }
            return (
              <div className="space-y-4">
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#5DD4B3] mb-2" data-testid="ef-source-group-primary">
                    PRIMARY / GOVERNMENT / UNDERLYING RECORDS · {primarySources.length}
                  </div>
                  <div className="space-y-2">{primarySources.map(renderSourceCard)}</div>
                </div>
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#9FA8D4] mb-2" data-testid="ef-source-group-reporting">
                    REPORTING / MEDIA / ANALYSIS · {reportingSources.length}
                  </div>
                  <div className="space-y-2">{reportingSources.map(renderSourceCard)}</div>
                </div>
              </div>
            );
          })()}
        </section>
      </div>
    </div>
  );
};

export default EpsteinEntity;
